// src/views/HelpCenterPage.jsx - VERSION DYNAMIQUE AVEC API (sans fallback)
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import PublicTopBar from "./PublicTopBar";
import Footer from "../components/Footer";
import { publicSiteWebService } from "../services/publicSiteWebService";

export default function HelpCenterPage() {
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({ parametres: null, contact: null });
  const [categories, setCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [openFaqId, setOpenFaqId] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [publicData, faqDoc] = await Promise.all([
          publicSiteWebService.getPublicData(),
          publicSiteWebService.getDocumentLegal("faq").catch(() => null),
        ]);
        setPageData({
          parametres: publicData?.parametres || null,
          contact: publicData?.contact || null,
        });
        if (faqDoc?.contenu) {
          try {
            const parsed = JSON.parse(faqDoc.contenu);
            setCategories(parsed.categories || []);
            setFaqs(parsed.faqs || []);
          } catch (e) {
            setCategories([]);
            setFaqs([]);
          }
        } else {
          setCategories([]);
          setFaqs([]);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const contact = pageData.contact || {};
  const email = contact.email || "contact@enumera.com";
  const telephone = contact.telephone || "";

  const filteredFaqs = useMemo(() => {
    return faqs.filter((f) => {
      const matchCategory = activeCategory ? f.categorie === activeCategory : true;
      const matchSearch = search.trim()
        ? (f.question + " " + f.reponse).toLowerCase().includes(search.trim().toLowerCase())
        : true;
      return matchCategory && matchSearch;
    });
  }, [faqs, search, activeCategory]);

  const toggleFaq = (id) => setOpenFaqId((prev) => (prev === id ? null : id));

  if (loading) {
    return (
      <div className="help-page">
        <PublicTopBar />
        <div
          className="loader-container"
          style={{ minHeight: "60vh", display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <div
            className="loader-spinner"
            style={{
              width: 48,
              height: 48,
              border: "4px solid #e2e8f0",
              borderTopColor: "#004080",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
        </div>
        <Footer />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`
        :root {
          --n-bg: #f8f9fa;
          --n-card: #ffffff;
          --n-ink: #0f1b2d;
          --n-mut: #64748b;
          --n-b: #e8e8ee;
          --n-b2: #d0d0da;
          --pr: #004080;
          --pr-lt: #0066CC;
          --pr-bg: #e6eef7;
        }

        .help-page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: var(--n-bg);
        }

        .help-hero {
          flex: 1;
          padding: 120px 20px 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .help-content {
          max-width: 900px;
          width: 100%;
          animation: fadeInUp 0.6s ease;
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .help-header { text-align: center; margin-bottom: 28px; }

        .help-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: var(--pr-bg);
          color: var(--pr);
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .help-badge i { font-size: 17px; }

        .help-title {
          font-size: 38px;
          font-weight: 700;
          color: var(--n-ink);
          margin: 0 0 8px;
          line-height: 1.2;
        }

        .help-title span { color: var(--pr); }

        .help-meta {
          font-size: 15px;
          color: var(--n-mut);
          max-width: 560px;
          margin: 0 auto 24px;
        }

        .help-search {
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--n-card);
          border: 1px solid var(--n-b);
          border-radius: 14px;
          padding: 14px 20px;
          max-width: 560px;
          margin: 0 auto 28px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04);
        }

        .help-search i { color: var(--pr); font-size: 18px; }

        .help-search input {
          border: none;
          outline: none;
          flex: 1;
          font-size: 14px;
          color: var(--n-ink);
          background: transparent;
        }

        .help-search input::placeholder { color: var(--n-mut); }

        .help-categories {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 12px;
          margin-bottom: 28px;
        }

        .help-category {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          background: var(--n-card);
          border: 1px solid var(--n-b);
          border-radius: 12px;
          padding: 16px 8px;
          cursor: pointer;
          transition: all 0.25s ease;
          font-size: 13px;
          font-weight: 600;
          color: var(--n-ink);
        }

        .help-category i { font-size: 22px; color: var(--pr); }

        .help-category:hover {
          border-color: var(--pr);
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 64, 128, 0.08);
        }

        .help-category.active {
          background: var(--pr);
          border-color: var(--pr);
          color: #fff;
        }

        .help-category.active i { color: #fff; }

        .help-body {
          background: var(--n-card);
          border: 1px solid var(--n-b);
          border-radius: 16px;
          padding: 12px 24px;
          margin-bottom: 28px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04);
        }

        .help-faq-item {
          border-bottom: 1px solid var(--n-b);
        }

        .help-faq-item:last-child { border-bottom: none; }

        .help-faq-question {
          width: 100%;
          background: none;
          border: none;
          text-align: left;
          padding: 18px 4px;
          font-size: 15px;
          font-weight: 600;
          color: var(--n-ink);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          cursor: pointer;
        }

        .help-faq-question i {
          color: var(--pr);
          font-size: 18px;
          transition: transform 0.25s ease;
          flex-shrink: 0;
        }

        .help-faq-question.open i { transform: rotate(180deg); }

        .help-faq-answer {
          padding: 0 4px 18px;
          font-size: 14px;
          line-height: 1.7;
          color: var(--n-mut);
        }

        .help-empty {
          padding: 32px 4px;
          text-align: center;
          color: var(--n-mut);
          font-size: 14px;
        }

        .help-actions {
          display: flex;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .help-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: var(--pr);
          color: #fff;
          border: none;
          border-radius: 12px;
          padding: 14px 28px;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .help-cta:hover {
          background: var(--pr-lt);
          color: #fff;
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 64, 128, 0.3);
        }

        .help-cta i { font-size: 17px; }

        .help-cta-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          color: var(--pr);
          border: 2px solid var(--pr);
          border-radius: 12px;
          padding: 12px 26px;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .help-cta-secondary:hover {
          background: var(--pr);
          color: #fff;
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0,64,128,0.15);
        }

        .loader-container { min-height: 60vh; display: flex; justify-content: center; align-items: center; }

        .loader-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #004080;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @media (max-width: 768px) {
          .help-title { font-size: 30px; }
          .help-hero { padding: 100px 16px 30px; }
          .help-categories { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 480px) {
          .help-title { font-size: 24px; }
          .help-hero { padding: 90px 16px 20px; }
          .help-search { padding: 12px 16px; }
          .help-faq-question { font-size: 14px; }
          .help-cta, .help-cta-secondary { width: 100%; justify-content: center; }
        }
      `}</style>

      <div className="help-page">
        <PublicTopBar />

        <section className="help-hero">
          <div className="help-content">
            <div className="help-header">
              <div className="help-badge">
                <i className="ti ti-help-circle"></i>
                <span>Centre d'aide</span>
              </div>

              <h1 className="help-title">
                Comment pouvons-nous <span>vous aider</span> ?
              </h1>

              <p className="help-meta">
                Retrouvez ici les réponses aux questions les plus fréquentes, classées par profil.
              </p>
            </div>

            <div className="help-search">
              <i className="ti ti-search"></i>
              <input
                type="text"
                placeholder="Rechercher une question…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {categories.length > 0 && (
              <div className="help-categories">
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className={`help-category ${activeCategory === cat.id ? "active" : ""}`}
                    onClick={() => setActiveCategory((prev) => (prev === cat.id ? null : cat.id))}
                  >
                    <i className={`ti ${cat.icon}`}></i>
                    <span>{cat.label}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="help-body">
              {filteredFaqs.length === 0 ? (
                <div className="help-empty">
                  {faqs.length === 0 ? (
                    "Le contenu de cette page n'est pas encore disponible."
                  ) : (
                    <>
                      Aucun résultat pour cette recherche. Essayez un autre mot-clé ou{" "}
                      <Link to="/contact">contactez-nous</Link> directement.
                    </>
                  )}
                </div>
              ) : (
                filteredFaqs.map((faq) => (
                  <div key={faq.id} className="help-faq-item">
                    <button
                      className={`help-faq-question ${openFaqId === faq.id ? "open" : ""}`}
                      onClick={() => toggleFaq(faq.id)}
                    >
                      <span>{faq.question}</span>
                      <i className="ti ti-chevron-down"></i>
                    </button>
                    {openFaqId === faq.id && (
                      <div className="help-faq-answer">{faq.reponse}</div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="help-actions">
              <Link to="/" className="help-cta">
                <i className="ti ti-arrow-left" aria-hidden="true"></i>
                Retour à l'accueil
              </Link>
              <a href={`mailto:${email}`} className="help-cta-secondary">
                <i className="ti ti-mail"></i>
                Écrire à {email}
              </a>
              {telephone && (
                <a href={`tel:${telephone.replace(/\s/g, "")}`} className="help-cta-secondary">
                  <i className="ti ti-phone"></i>
                  {telephone}
                </a>
              )}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}